/* eslint-disable max-len */
import { LitElement, html } from 'lit-element';
import { Chart, registerables } from 'chart.js';
import objectHash from 'object-hash';
import annotationPlugin from 'chartjs-plugin-annotation';
import style from './fc-chart.css';
import 'chartjs-adapter-moment';
import generateBasicChartJSConfig from './chart-config-builders/basic-config-builder';
import generateChartJsConfigOptions from './chart-config-builders/config-options-builder';
import generateSankeyConfig from './chart-config-builders/sankey-config-builder';
import generateFunnelConfig from './chart-config-builders/funnel-config-builder';
import generateTooltipConfig from './chart-config-builders/tooltip-config-builder';
import insertYAxisTicks from './chart-config-builders/y-axis-symbol-config-builder';
import insertTimeAxisTicks from './chart-config-builders/time-axis-config-builder';
import periodOverPeriodBuilder from './chart-config-builders/period-over-period-builder';
import insertXAxisTicks from './chart-config-builders/x-axis-config-builder';
import insertAnnotations from './chart-config-builders/annotation-builder';
import doughnutBuilder from './chart-config-builders/doughnut-builder';
import { registerSeriesClickEvent } from './chart-config-builders/register-series-click-event';
import colors from '@forter/styles/colors.css';
import { updateColorsInDatasets } from './chart-config-builders/dataset-color-config-builder';
import htmlLegendBuilder from './chart-config-builders/html-legend-builder';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';
import { SankeyController, Flow } from './plugins/sankey';
import { funnelPlugin } from './plugins/funnel';
import sparklineConfigBuilder from './chart-config-builders/sparkline-config-builder';

/**
 * An element by Forter
 * <!-- Author: Omer Anati <omer.anati@forter.com> -->
 *
 * ## Usage
 *
 * ```html
 * <script>
 *   import '@forter/chart';
 * </script>
 *
 * <fc-chart
 *  config={"xAxisType":"SINGLE_TIME_FIELD","series":[{"seriesType":"LINE","label":"AGGS.APPROVED.LABEL","aggregation":{"key":"APPROVED","filters":{},"dimensions":[{"key":"TRANS_TIME","label":"DIMENSION.TRANS_TIME.LABEL"}],"data":[{"dimensions":["2021/07/02"],"value":10,"formattedValue":10,"aggregationLabel":"Approved Tx"},{"dimensions":["2021/07/05"],"value":11,"formattedValue":11,"aggregationLabel":"Approved Tx"},{"dimensions":["2021/07/11"],"value":10,"formattedValue":10,"aggregationLabel":"Approved Tx"},{"dimensions":["2021/07/18"],"value":12,"formattedValue":12,"aggregationLabel":"Approved Tx"}]}}]}
 * >
 * </fc-chart>
 * ```
 *
* @element   fc-chart
 * @cssprop --fc-chart-disabled-color -  color.
 * @cssprop --fc-chart-disabled-color - disabled color. example: `gray`
 */
export class FcChart extends LitElement {
  /**
   * If the element is disabled
   * @type {Boolean}
   * @attr
   */
  static get properties() {
    return {
      config: { type: Object, value: {} },
      disabled: { type: Boolean, value: false },
      type: { type: String, value: 'line' },
      configHash: { type: String },
      loading: { type: Boolean, value: false },
      previewMode: { type: Boolean, attribute: 'preview-mode', value: false },
      isLegendBoxOpen: { type: Boolean, value: true },
      isLegendExpandIndicatorVisible: { type: Boolean, value: true },
      isLegendHidden: { type: Boolean, value: false }
    };
  }

  /** @inheritdoc */
  static is = 'fc-chart';

  /** @inheritdoc */
  static styles = [style, colors];

  constructor() {
    super();
    this.config = {};
    this.type = 'line';
    this.configHash = null;
    this.resizeObserver = null;
    this.minExpandHeight = 46;
    this.isLegendExpandIndicatorVisible = true;
    this.isLegendHidden = false;
  }

  get tooltipElement() {
    return this.shadowRoot.getElementById('tooltip');
  }

  get canvas() {
    return this.shadowRoot.getElementById('chart');
  }


  firstUpdated() {
    this.context = this.canvas.getContext('2d');
    Chart.register([annotationPlugin]);
    Chart.register(TreemapController, TreemapElement);
    Chart.register(SankeyController, Flow);
    Chart.register(...registerables);
    Chart.register(funnelPlugin);

    // Update label formatters after chart animations finish (animation duration: 1s)
    setTimeout(() => this.chart?.update(0), 1000);
  }

  hasConfigChanged(config) {
    if (this.configHash) {
      const newConfigHash = objectHash(config);
      if (newConfigHash === this.configHash) {
        return false;
      }
    }
    this.configHash = objectHash(config);
    return true;
  }

  updated(_changedProperties) {
    super.updated(_changedProperties);
    const { config } = this;
    if (!this.hasConfigChanged(config)) {
      return;
    }
    let updatesSeries;
    _changedProperties.forEach(value => {
      updatesSeries = value;
    });
    if (this.chart && updatesSeries !== config) {
      this.chart.destroy();
    }
    this.chartJsConfig = this.buildChartJSConfig(config);
    this.chart = config && new Chart(this.context, this.chartJsConfig);
  }

  buildChartJSConfig(config) {
    const builders = this.getConfigBuilders(config);
    return builders.reduce((currentChartJSConfig, currentBuilder) => {
      return currentBuilder
        ? currentBuilder.bind(this)(config, currentChartJSConfig, this)
        : currentChartJSConfig;
    }, {});
  }

  getConfigBuilders() {
    return [
      generateBasicChartJSConfig,
      generateChartJsConfigOptions,
      generateSankeyConfig,
      generateFunnelConfig,
      insertYAxisTicks,
      insertTimeAxisTicks,
      updateColorsInDatasets,
      periodOverPeriodBuilder,
      registerSeriesClickEvent,
      insertXAxisTicks,
      htmlLegendBuilder,
      insertAnnotations,
      doughnutBuilder,
      generateTooltipConfig,
      sparklineConfigBuilder,
    ];
  }

  renderLegendSkeleton() {
    return Array.from({ length: 8 }).map(() => html`
      <li class="legend-item">
        <div class="legend-color-box"></div>
        <span class="skeleton-legend-label"></span>
      </li>
    `);
  }

  toggleLegendBox() {
    this.isLegendBoxOpen = !this.isLegendBoxOpen;
  }

  renderLegend() {
    return html`
      <div id="legend-container" ?expandable=${this.isLegendExpandIndicatorVisible} ?open=${this.isLegendBoxOpen}>
        ${!this.loading ? html`
          <button class="expand-button" @click=${this.toggleLegendBox}>
            <fc-icon icon="arrow-up"></fc-icon>
          </button>
        ` : ''}
        <div class="legend-scroll-container" ?hasScroll=${this.isLegendBoxOpen}>
          <ul id="real-legend-container" class="legend-container"></ul>
          <ul id="skeleton-legend-container" class="skeleton-legend-container">
            ${this.renderLegendSkeleton()}
          </ul>
        </div>
      </div>
    `;
  }

  render() {
    const seriesType = this.config?.series?.at(0)?.seriesType?.toLowerCase();
    return html`
      <div class="chart-wrapper" data-series-type="${seriesType}">
        <canvas id="chart"></canvas>
      </div>
      ${this.isLegendHidden ? null : this.renderLegend()}

      <div id="tooltip" class="tooltip hide"></div>
    `;
  }
}
